import { locateProxy } from '#~/agent/utils.js';
import { useListener } from '#~/command/hooks.js';
import { Command } from '#~/command/index.js';
import { Toggles as GridLines } from '#~/control/components/grid-lines/index.js';
import { Toolbar } from '#~/control/components/toolbar/index.js';
import type { ToolsMap } from '#~/control/constants.js';
import { toEffectiveTool } from '#~/control/effective-tool.js';
import { useGridLinesPref, useToolPref, useToolVariants } from '#~/control/hooks.js';
import { Tool, Tools } from '#~/control/index.js';
import { buzz } from '#~/gamepad/index.js';
import { useTemplate } from '#~/device/registry/hooks.js';
import {
  useArrangement,
  useCalibration,
  useDeviceIds,
  useRoom,
  useSelectedVideo
} from '#~/room/hooks.js';
import { Arrangement } from '#~/video/components/arrangement/index.js';
import { Arrangement as M_Arrangement } from '#~/video/index.js';
import { Root } from './root.jsx';
import type { Props } from './types.js';
import { isCalibrated, isCalibrating } from '@studio/models/control/calibration';
import { toAccessor } from '@studio/react-utils';
import { toEnumValues, toObjectEntries } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';

/** `<TopTray>` component. */
export const TopTray = (props: Props): ReactElement => {
  const videoCount = useDeviceIds().videoIds.length;
  const arrangement = toAccessor(useArrangement());
  const gridLines = toAccessor(useGridLinesPref());
  const tool = toAccessor(useToolPref());
  const toolVaraints = useToolVariants();
  const [selectedId] = useSelectedVideo();
  const calibration = useCalibration(selectedId);

  // ENG-4495 C4: the tools memo sources state from useRoom/useCalibration selectors, not a
  // template — so resolve the selected device's model template here for the driver-identity bit.
  // D3: tri-state, permissive on absence — only a LOADED generic === true blocks tools.
  const modelId = useRoom((µ) => (selectedId ? µ.devices[selectedId]?.modelId : undefined));
  const template = useTemplate(modelId);
  const bGeneric = template?.model?.generic === true;

  let canPan = false;
  let canTilt = false;
  let canZoom = false;

  if (selectedId && props.canControl) {
    const proxy = locateProxy(selectedId);

    canPan =
      !!proxy?.state.status?.device[selectedId]?.capabilities?.movement?.vectors?.pan?.absoluteMove;
    canTilt =
      !!proxy?.state.status?.device[selectedId]?.capabilities?.movement?.vectors?.tilt
        ?.absoluteMove;
    canZoom =
      !!proxy?.state.status?.device[selectedId]?.capabilities?.movement?.vectors?.zoom
        ?.absoluteMove;
  }

  const [loadedTools, setTools] = useState<ToolsMap>({} as ToolsMap);

  useEffect(() => {
    (async () => {
      const tools = await Tools();

      setTools(tools as ToolsMap);
    })();
  }, []);

  const available = {
    // The available arrangements are dependent on the number of video devices.
    arrangements: useMemo(
      () => [
        { arrangement: M_Arrangement.Single },
        { arrangement: M_Arrangement.Multiple, disabled: videoCount === 1 }
      ],
      [videoCount]
    ),

    // The available tools a dependent on whether the selected device has been or is being calibrated.
    tools: useMemo(() => {
      const bCalibrating = isCalibrating(calibration);
      const bCalibrated = isCalibrated(calibration);

      return toEnumValues(Tool)
        .sort((a, b) => loadedTools[a]?.order - loadedTools[b]?.order)
        .map((µ) => {
          /*
           * If a device is being calibrated, no tools are available.  Otherwise, if the device
           * is not calibrated, certain tools are not available.
           */
          const disabled = bCalibrating
            ? true
            : // ENG-4495 C4/D4: a driverless (generic) model blocks the driver-dependent tools
              // regardless of calibration state — this beats bCalibrated and covers the historical
              // OK_GENERIC other-other devices. Joystick/Zoom stay capability-gated only (D5).
              (bGeneric && [Tool.Center, Tool.FastFrame, Tool.Tracking].includes(µ)) ||
              !!(!bCalibrated && [Tool.Center, Tool.FastFrame, Tool.Tracking].includes(µ)) ||
              ([Tool.Center, Tool.FastFrame, Tool.Tracking, Tool.Joystick].includes(µ) &&
                !canPan &&
                !canTilt) ||
              ([Tool.FastFrame, Tool.Zoom].includes(µ) && !canZoom);

          return { disabled, tool: µ };
        });
      // ENG-4495 C4: `bGeneric` is the new gating input; `canTilt` completes the capability inputs
      // the memo already reads (react-hooks/exhaustive-deps — a missing dep = a stale tool gate).
    }, [bGeneric, calibration, canPan, canTilt, canZoom, loadedTools])
  };

  // Command listener
  //
  // useMemo with [loadedTools] instead of useLazyRef: the previous
  // useLazyRef implementation ran its initializer exactly once on first
  // render — when loadedTools is still the empty {} initial state from
  // useState. The async useEffect that populates loadedTools never caused
  // useLazyRef to recompute, so commandMap stayed permanently {} and every
  // toolbar hotkey (J, Z, C, F, etc.) fell through the lookup. useMemo
  // recomputes when loadedTools lands.
  const commandMap = useMemo<{ [key in Command]?: [Tool, T_Maybe<Command>] }>(() => {
    const entries = toObjectEntries(loadedTools).flatMap(([k, v]) => {
      const options = v.options.map((opt) => [opt.command, [k, opt.command]]);

      return [...options, [v.command, [k, undefined]]] as [Command, [Tool, T_Maybe<Command>]][];
    });

    return Object.fromEntries(entries);
  }, [loadedTools]);

  useListener((directive) => {
    const { command } = directive;

    if (command === Command.VIDEO_ARRANGEMENT) {
      const nextArrangement =
        arrangement.value === M_Arrangement.Single ? M_Arrangement.Multiple : M_Arrangement.Single;

      if (arrangement.value !== nextArrangement) {
        arrangement.setValue(nextArrangement);
      } else {
        buzz(directive);
      }

      return;
    }

    if (commandMap[command]) {
      const entry = commandMap[command];

      if (!entry) {
        return;
      }

      // ENG-4495 C4/D4: the button-disable (C4) does not reach this command listener — a keyboard
      // shortcut or a dropdown submenu maps straight to a tool and activates it. A tool whose computed
      // gate is `disabled` (e.g. Center/FastFrame/Tracking on a driverless generic model, or any
      // uncalibrated magic tool) must not be selectable via command either, or the magic tool runs
      // anyway. Reuse the same `available.tools` disabled state; TOOL_NEXT/TOOL_PREV already skip
      // disabled tools below.
      if (available.tools.find((µ) => µ.tool === entry[0])?.disabled) {
        buzz(directive);

        return;
      }

      tool.setValue(entry[0]);

      toolVaraints[1](entry[0], entry[1] ?? command);

      return;
    }

    if ([Command.TOOL_NEXT, Command.TOOL_PREV].includes(command)) {
      const { tools } = available;
      const { length } = tools;
      const delta = command === Command.TOOL_NEXT ? 1 : -1;

      // What is the index of the next tool?
      const currentIndex = Math.max(
        tools.findIndex((µ) => µ.tool === tool.value),
        0
      );

      let nextIndex = currentIndex;
      let nextTool: T_Maybe<Tool>;

      while (!nextTool) {
        nextIndex = (nextIndex + delta + length) % length;

        const candidate = tools[nextIndex];

        if (candidate && !tools[currentIndex]?.disabled) {
          nextTool = candidate.tool;

          break;
        }

        if (nextIndex === currentIndex) {
          // All the tools must be disabled.
          break;
        }
      }

      if (nextTool) {
        tool.setValue(nextTool);
      } else {
        buzz(directive);
      }
    }
  });

  // ENG-4495 D5: the top-tray highlight must match the EFFECTIVE selection, not the raw pref. On a
  // generic device a persisted magic tool (default Tool.Center) is permanently disabled (C4/D4), so
  // highlighting it lights up a dead button. Derive the effective tool from the SAME `available.tools`
  // gate — falling back to the first usable manual tool (Joystick, else Zoom, …) — and hand the
  // Toolbar a derived accessor: the VALUE is overridden for the highlight, but `setValue` still writes
  // the real global pref, so clicking a tool persists as before and returning to a branded camera
  // restores the user's choice (no pref clobber). Non-generic devices are unchanged (AC4).
  const effectiveSelection = toAccessor(
    toEffectiveTool(tool.value, bGeneric, available.tools),
    tool.setValue
  );

  return (
    <Root {...props}>
      {!!videoCount && props.canControl && (
        <>
          <Arrangement available={available.arrangements} selection={arrangement} />
          <Toolbar
            available={available.tools}
            selection={effectiveSelection}
            variant={toolVaraints}
          />
          <GridLines gridLines={gridLines} />
        </>
      )}
    </Root>
  );
};
